import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
} from '@mui/icons-material';
import { useGetAuditTrail } from '@/features/audit-logs/api';
import { AuditLogDto } from '@aphp-ema/common/audit';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { getActionIconType, getActionColor } from '../utils/auditTrail.utils';

const getActionIcon = (action: string) => {
  const iconType = getActionIconType(action);
  switch (iconType) {
    case 'CREATE':
      return <AddIcon />;
    case 'UPDATE':
      return <EditIcon />;
    case 'DELETE':
      return <DeleteIcon />;
    case 'PUBLISH':
      return <PublishIcon />;
    default:
      return <EditIcon />;
  }
};

interface AuditTrailProps {
  resourceId: string;
  title?: string;
}

export const AuditTrail: React.FC<AuditTrailProps> = ({
  resourceId,
  title,
}) => {
  const { t } = useTranslation();
  const { data: auditLogs, isLoading, error } = useGetAuditTrail(resourceId);

  if (!resourceId) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="text.secondary">
          {t('pages.auditLogs.messages.noResourceId')}
        </Typography>
      </Paper>
    );
  }

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">
          {t('pages.auditLogs.messages.historyLoadError')}: {error.message}
        </Alert>
      </Paper>
    );
  }

  if (!auditLogs || auditLogs.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {title || t('pages.auditLogs.trail.title')}
        </Typography>
        <Typography color="text.secondary">
          {t('pages.auditLogs.messages.noHistory')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title || t('pages.auditLogs.trail.title')}
      </Typography>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        {t('pages.auditLogs.fields.resourceId')}: {resourceId}
      </Typography>

      <Timeline>
        {auditLogs.map((log: AuditLogDto, index: number) => (
          <TimelineItem key={log.id}>
            <TimelineSeparator>
              <TimelineDot color={getActionColor(log.action)}>
                {getActionIcon(log.action)}
              </TimelineDot>
              {index < auditLogs.length - 1 && <TimelineConnector />}
            </TimelineSeparator>

            <TimelineContent>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Chip
                      label={t(
                        `pages.auditLogs.actions.${log.action}`,
                        log.action,
                      )}
                      size="small"
                      color={getActionColor(log.action)}
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(log.timestamp).format('DD/MM/YYYY HH:mm:ss')}
                    </Typography>
                  </Box>

                  <Typography variant="body2" gutterBottom>
                    <strong>{t('pages.auditLogs.trail.user')}:</strong>{' '}
                    {log.userEmail}
                  </Typography>

                  {log.ipAddress && (
                    <Typography variant="body2" gutterBottom>
                      <strong>{t('pages.auditLogs.trail.ip')}:</strong>{' '}
                      {log.ipAddress}
                    </Typography>
                  )}

                  {log.details && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('pages.auditLogs.trail.details')}:
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1,
                          mt: 0.5,
                          backgroundColor: 'grey.50',
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          overflow: 'auto',
                        }}
                      >
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </Paper>
                    </Box>
                  )}

                  {log.sessionId && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 1 }}
                    >
                      {t('pages.auditLogs.trail.session')}: {log.sessionId}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
    </Paper>
  );
};
